import { Input, InputProps, InputRef } from "antd";
import { createContext, KeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

interface EnterInputInfo {
  event: KeyboardEvent<HTMLInputElement>;
  order: string;
  data: any;
}

interface Props {
  children?: ReactNode;
  onChangeOrder?: (info: EnterInputInfo) => void;
}

interface CustomInputProps extends InputProps {
  onEnter?: (info: EnterInputInfo) => void;
}

type DocumentOrderContextType = {
  selectedId: string;
  changeSelectedId: (id: string) => void;
  onPressEnterInput: (info: EnterInputInfo) => void;
  OrderInput: (props: CustomInputProps) => JSX.Element;
};

function checkIsNumber(str: string) {
  const regex = /^-?\d+(\.\d+)?$/;
  return regex.test(str);
}

const OrderInput = (props: CustomInputProps) => {
  const { onEnter = () => {}, ...inputProps } = props;
  const [status, setStatus] = useState<InputProps["status"]>();
  const [order, setOrder] = useState<string>(props.defaultValue as string);
  const inputRef = useRef<InputRef | null>(null);

  const onPressEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    const isNumber = checkIsNumber(order);
    if (!isNumber) {
      setStatus("error");
      return;
    }
    setStatus(undefined);
    onEnter({ event: e, order, data: {} });
  };

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.focus();
  }, []);

  return (
    <Input
      status={status}
      ref={inputRef}
      onClick={(e) => e.stopPropagation()}
      value={order}
      onChange={(e) => setOrder(e.target.value)}
      onPressEnter={onPressEnter}
      {...inputProps}
    />
  );
};

export const DocumentOrderContext = createContext<DocumentOrderContextType>({
  selectedId: "",
  changeSelectedId: () => {},
  onPressEnterInput: () => {},
  OrderInput,
});

const DocumentOrderProvider = (props: Props) => {
  const { onChangeOrder } = props;
  const [selectedId, setSelectedId] = useState("");

  const changeSelectedId = useCallback((id: string) => setSelectedId(id), []);

  const onPressEnterInput = useCallback(
    (info: EnterInputInfo) => {
      if (onChangeOrder) onChangeOrder(info);
      changeSelectedId("");
    },
    [changeSelectedId, onChangeOrder],
  );

  const value: DocumentOrderContextType = useMemo(
    () => ({
      selectedId,
      changeSelectedId,
      onPressEnterInput,
      OrderInput,
    }),
    [selectedId, changeSelectedId, onPressEnterInput],
  );

  return <DocumentOrderContext.Provider value={value}>{props.children}</DocumentOrderContext.Provider>;
};

export default DocumentOrderProvider;
